import { createContext, ReactNode, useState, useEffect, useContext } from "react"
import { watchAccount } from "@wagmi/core"
import { ConnectEthereumUseCaseInput } from "../domain/ethereum/UseCases/ConnectEthereumUseCase"
import { WalletState } from "../domain/ethereum/Models/WalletState"
import { publicClient } from "../js/viem"
import { wagmiConfig } from "../js/wagmi"
import { useDocumentVisibility } from "../hooks/useDocumentVisibility"

window.publicClient = window.publicClient ?? publicClient
window.wagmiConfig = window.wagmiConfig ?? wagmiConfig

interface EthereumContextInterface {
  wallet: WalletState
  connectWallet: ({ connector }: ConnectEthereumUseCaseInput) => Promise<WalletState>
  disconnectWallet: () => Promise<WalletState>
  switchChain: () => Promise<WalletState>
  waitForNextBlockNumber: () => Promise<number>
}

const initialState: EthereumContextInterface = {
  wallet: WalletState.empty(),
  connectWallet: async () => WalletState.empty(),
  disconnectWallet: async () => WalletState.empty(),
  switchChain: async () => WalletState.empty(),
  waitForNextBlockNumber: async () => 0,
}

interface EthereumProviderProps {
  children: ReactNode
}

const Context = createContext<EthereumContextInterface>(initialState)

export function EthereumContext({ children }: EthereumProviderProps) {
  const [wallet, setWallet] = useState<WalletState>(initialState.wallet)
  const isVisible = useDocumentVisibility()

  const setLoadingState = () => {
    if (wallet.isConnecting) return
    setWallet(WalletState.create({ status: WalletState.STATUS.CONNECTING }))
  }

  const setEmptyState = () => setWallet(WalletState.empty())

  const updateWalletState = async () => {
    return window.domain.GetWalletStateEthereumUseCase.execute()
      .then((updatedWallet) => {
        if (wallet.account && wallet.account !== updatedWallet.account) window.location.reload()
        if (wallet.serialize() === updatedWallet.serialize()) return

        setWallet(updatedWallet)
        return updatedWallet
      })
      .catch((error) => {
        setEmptyState()
        throw error
      })
  }

  const connectWallet = async ({ connector }: ConnectEthereumUseCaseInput) => {
    setLoadingState()
    return window.domain.ConnectEthereumUseCase.execute({ connector })
      .then((wallet) => {
        setWallet(wallet)
        return wallet
      })
      .catch((error) => {
        setEmptyState()
        throw error
      })
  }

  const disconnectWallet = async () => {
    return window.domain.DisconnectEthereumUseCase.execute()
      .then((wallet) => {
        setWallet(wallet)
        return wallet
      })
      .catch((error) => {
        setEmptyState()
        throw error
      })
  }

  const switchChain = async () => {
    return window.domain.SwitchChainEthereumUseCase.execute()
      .then((wallet) => {
        setWallet(wallet)
        return wallet
      })
      .catch((error) => {
        setEmptyState()
        throw error
      })
  }

  const waitForNextBlockNumber = async (): Promise<number> => {
    return new Promise((resolve, reject) => {
      let currentBlockNumber: number | undefined

      try {
        // Start watching for block numbers
        const unwatch = window.publicClient.watchBlockNumber({
          onBlockNumber: (blockNumber) => {
            if (currentBlockNumber === undefined) {
              // Capture the initial block number
              currentBlockNumber = Number(blockNumber)
            } else if (blockNumber > currentBlockNumber) {
              // Resolve only when the block number increments
              unwatch()
              resolve(Number(blockNumber))
            }
          },
          onError: (error) => {
            unwatch()
            reject(error)
          },
        })
      } catch (error) {
        reject(error)
      }
    })
  }

  // Initial silent connect
  useEffect(() => {
    setLoadingState()
    window.domain.ReconnectEthereumUseCase.execute()
      .then(updateWalletState)
      .catch((error) => {
        setEmptyState()
        throw error
      })
  }, [])

  // Browser wallet events
  useEffect(() => {
    if (wallet.isAbstracted) return

    return watchAccount(window.wagmiConfig, {
      onChange() {
        if (!wallet.isConnecting) updateWalletState()
      },
    })
  }, [wallet])

  // RPC events
  useEffect(() => {
    const unwatch = window.publicClient.watchBlockNumber({
      onBlockNumber: (blockNumber) => {
        console.log("__________", Number(blockNumber), "__________")
        window.blockNumber = Number(blockNumber)
      },
    })

    if (!isVisible) unwatch()

    return unwatch
  }, [isVisible])

  return (
    <Context.Provider
      value={{
        wallet,
        connectWallet,
        disconnectWallet,
        switchChain,
        waitForNextBlockNumber,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const useEthereum = function () {
  const context = useContext(Context)
  if (context === undefined) {
    throw new Error(`useEthereum must be used within a EthereumContextProvider`)
  }
  return context
}
